import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import socketManager from '../lib/socket';
import api from '../lib/api';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Filter,
  ExternalLink,
  Info
} from 'lucide-react';

// Format timestamp with milliseconds for precise timing
function formatTimestamp(date) {
  if (!date) return '—';
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }) + '.' + d.getMilliseconds().toString().padStart(3, '0');
}

// Format latency in human readable form
function formatLatency(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// Get status badge styling
function getStatusBadge(status) {
  switch (status) {
    case 'filled':
      return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle };
    case 'failed':
    case 'rejected':
      return { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle };
    case 'pending':
    case 'submitted':
      return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock };
    case 'partial':
      return { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertTriangle };
    default:
      return { bg: 'bg-dark-600', text: 'text-dark-400', icon: Info };
  }
}

// Get failure code explanation
function getFailureExplanation(code, reason) {
  // Check for invalid signature error
  if (code === 'INVALID_SIGNATURE' || reason?.toLowerCase().includes('invalid signature')) {
    return '⚠️ Invalid signature - Go to Settings → Wallet and change your Signature Type (try Type 2)';
  }
  
  const explanations = {
    'PRICE_TOLERANCE': 'Price moved too far from trader\'s execution price',
    'MAX_PRICE_FILTER': 'Market price exceeded your maximum price filter',
    'MIN_PRICE_FILTER': 'Market price fell below your minimum price filter',
    'FOK_NO_FILL': 'Fill-or-Kill order couldn\'t be fully filled',
    'NO_LIQUIDITY': 'Not enough liquidity in the order book',
    'NO_POSITION': 'No position to sell (trader sold but you never bought)',
    'RISK_SIZE_ZERO': 'Risk management reduced trade size to zero',
    'INSUFFICIENT_BALANCE': '💰 Not enough USDC.e balance or token allowance. Make sure your wallet has USDC.e (not regular USDC) and some POL for gas fees.',
    'ORDER_REJECTED': 'Order was rejected by the exchange',
    'INVALID_SIGNATURE': '⚠️ Invalid signature - Go to Settings → Wallet and change your Signature Type'
  };
  return explanations[code] || code;
}

// Check if error is an invalid signature error
function isInvalidSignatureError(trade) {
  const reason = trade.executionDetails?.failureReason || '';
  const code = trade.executionDetails?.failureCode || '';
  return code === 'INVALID_SIGNATURE' || 
         reason.toLowerCase().includes('invalid signature') ||
         reason.toLowerCase().includes('invalid sig');
}

function TradeRow({ trade, expanded, onToggle }) {
  const statusStyle = getStatusBadge(trade.status);
  const StatusIcon = statusStyle.icon;
  const isFailed = trade.status === 'failed' || trade.status === 'rejected';
  const isSuccess = trade.status === 'filled';
  
  const execDetails = trade.executionDetails || {};
  // Show dropdown if there's failure info, retry history, retry count, or error message
  const hasDetails = execDetails.failureReason || execDetails.failureCode || 
                     trade.retryHistory?.length > 0 || trade.retryCount > 0 || 
                     trade.errorMessage || trade.status === 'pending' || trade.status === 'failed';

  return (
    <div className={`border-b border-dark-700 last:border-0 ${expanded ? 'bg-dark-800/50' : ''}`}>
      {/* Main Row */}
      <div 
        className="p-3 cursor-pointer hover:bg-dark-800/30 transition-colors"
        onClick={() => hasDetails && onToggle()}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Left: Status + Market */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={`p-1.5 rounded-lg ${statusStyle.bg}`}>
              <StatusIcon className={`w-4 h-4 ${statusStyle.text}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  trade.side === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {trade.side}
                </span>
                <span className="text-xs text-dark-400">{trade.orderType || 'FOK'}</span>
                {trade.tradeType === 'copy' && (
                  <span className="text-xs text-primary-400">COPY</span>
                )}
              </div>
              <p className="text-sm font-medium mt-1 truncate">
                {trade.market?.question || 'Unknown Market'}
              </p>
              <p className="text-xs text-dark-400 mt-0.5">
                {trade.market?.outcome || 'Yes'} • {formatTimestamp(trade.createdAt)}
              </p>
            </div>
          </div>

          {/* Right: Price/Size Info */}
          <div className="text-right flex-shrink-0">
            {isSuccess ? (
              <>
                <p className="text-sm font-medium text-emerald-400">
                  {trade.filledSize?.toFixed(2)} shares
                </p>
                <p className="text-xs text-dark-400">
                  @ ${trade.avgFillPrice?.toFixed(3)} = ${trade.totalValue?.toFixed(2)}
                </p>
                {trade.slippage !== 0 && trade.slippage != null && (
                  <p className={`text-xs ${trade.slippage > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {trade.slippage > 0 ? '+' : ''}{trade.slippage.toFixed(2)}% slippage
                  </p>
                )}
              </>
            ) : isFailed ? (
              <>
                <p className="text-sm font-medium text-red-400">
                  {execDetails.failureCode || 'FAILED'}
                </p>
                <p className="text-xs text-dark-400">
                  {trade.retryCount > 0 ? `${trade.retryCount} retries` : 'No retries'}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-yellow-400">
                  {trade.status?.toUpperCase()}
                </p>
                <p className="text-xs text-dark-400">
                  ${trade.requestedSize?.toFixed(2)} requested
                </p>
              </>
            )}
            
            {/* Expand indicator */}
            {hasDetails && (
              <div className="mt-1">
                {expanded ? (
                  <ChevronUp className="w-4 h-4 text-dark-500 inline" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-dark-500 inline" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && hasDetails && (
        <div className="px-3 pb-3 space-y-3 animate-fade-in">
          {/* Failure Reason - show from executionDetails or errorMessage */}
          {(execDetails.failureReason || trade.errorMessage) && (
            <div className={`p-3 rounded-lg ${isInvalidSignatureError({ executionDetails: execDetails }) ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
              <p className={`text-xs font-medium mb-1 ${isInvalidSignatureError({ executionDetails: execDetails }) ? 'text-amber-400' : 'text-red-400'}`}>
                {getFailureExplanation(execDetails.failureCode, execDetails.failureReason || trade.errorMessage)}
              </p>
              <p className="text-xs text-dark-300">
                {execDetails.failureReason || trade.errorMessage}
              </p>
            </div>
          )}
          
          {/* Show pending status info */}
          {trade.status === 'pending' && !execDetails.failureReason && !trade.errorMessage && (
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-xs font-medium text-yellow-400 mb-1">Order Pending</p>
              <p className="text-xs text-dark-300">
                Waiting for order to fill on Polymarket. GTC orders may take time to match.
              </p>
            </div>
          )}
          
          {/* Show retry count if no other details */}
          {trade.retryCount > 0 && !execDetails.failureReason && !trade.errorMessage && trade.status !== 'pending' && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs font-medium text-red-400 mb-1">Trade Failed After {trade.retryCount} Retries</p>
              <p className="text-xs text-dark-300">
                The order could not be filled after multiple attempts. Check liquidity and price conditions.
              </p>
            </div>
          )}

          {/* Execution Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {execDetails.traderPrice != null && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Trader Price</p>
                <p className="font-medium">${execDetails.traderPrice?.toFixed(3)}</p>
              </div>
            )}
            {execDetails.marketPrice != null && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Market Price</p>
                <p className="font-medium">${execDetails.marketPrice?.toFixed(3)}</p>
              </div>
            )}
            {execDetails.priceDelta != null && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Price Delta</p>
                <p className={`font-medium ${execDetails.priceDelta > 0.02 ? 'text-red-400' : ''}`}>
                  {(execDetails.priceDelta * 100).toFixed(1)}¢
                </p>
              </div>
            )}
            {execDetails.priceToleranceUsed != null && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Tolerance</p>
                <p className="font-medium">{(execDetails.priceToleranceUsed * 100).toFixed(1)}¢</p>
              </div>
            )}
            {execDetails.executionLatencyMs != null && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Latency</p>
                <p className="font-medium">{formatLatency(execDetails.executionLatencyMs)}</p>
              </div>
            )}
            {execDetails.filterTriggered && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Filter</p>
                <p className="font-medium text-red-400">{execDetails.filterTriggered}</p>
              </div>
            )}
          </div>

          {/* Timing Details */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {execDetails.traderTradeTime && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Trader Executed</p>
                <p className="font-medium">{formatTimestamp(execDetails.traderTradeTime)}</p>
              </div>
            )}
            {execDetails.copyAttemptTime && (
              <div className="p-2 rounded bg-dark-700/50">
                <p className="text-dark-500">Copy Attempted</p>
                <p className="font-medium">{formatTimestamp(execDetails.copyAttemptTime)}</p>
              </div>
            )}
          </div>

          {/* Retry History */}
          {trade.retryHistory?.length > 0 && (
            <div className="p-3 rounded-lg bg-dark-700/30">
              <p className="text-xs font-medium text-dark-400 mb-2">Retry History</p>
              <div className="space-y-1">
                {trade.retryHistory.map((retry, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <span className="text-dark-500">
                      Attempt {retry.attempt}: {retry.reason}
                    </span>
                    <span className="text-dark-400">
                      {retry.marketPrice ? `$${retry.marketPrice.toFixed(3)}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trade Source Info */}
          {trade.copiedFrom?.traderAddress && (
            <div className="flex items-center gap-2 text-xs text-dark-400">
              <span>Source:</span>
              {/* Determine if Sentinel or Whale based on source field or traderAddress */}
              {trade.source === 'sentinel' || trade.copiedFrom.traderAddress?.includes('sentinel') ? (
                <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 font-medium">
                  🤖 Sentinel
                </span>
              ) : trade.copiedFrom.traderAddress?.includes('whale_mode') ? (
                <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-medium">
                  🐋 Whale Mode
                </span>
              ) : (
                <code className="px-2 py-0.5 rounded bg-dark-700 font-mono">
                  {trade.copiedFrom.traderAddress.slice(0, 10)}...{trade.copiedFrom.traderAddress.slice(-6)}
                </code>
              )}
              {trade.copiedFrom.originalPrice && (
                <span>@ ${trade.copiedFrom.originalPrice.toFixed(3)}</span>
              )}
            </div>
          )}

          {/* Error Message */}
          {trade.errorMessage && !execDetails.failureReason && (
            <div className="p-2 rounded bg-dark-700/50 text-xs text-dark-300">
              <span className="text-dark-500">Error: </span>
              {trade.errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TradeActivityLog({ configId = null, limit = 100 }) {
  const { user } = useAuthStore();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const containerRef = useRef(null);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit, status: statusFilter !== 'all' ? statusFilter : undefined };
      if (configId) params.copyConfigId = configId;
      const response = await api.get('/positions/trades', { params });
      setTrades(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch trades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [configId, statusFilter]);

  // Real-time updates via socket
  useEffect(() => {
    if (!user?._id) return;

    const handleTradeExecuted = (trade) => {
      setTrades(prev => {
        const exists = prev.find(t => t._id === trade._id);
        if (exists) {
          return prev.map(t => t._id === trade._id ? trade : t);
        }
        return [trade, ...prev].slice(0, limit);
      });
      if (autoScroll && containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    };

    const handleTradeFailed = (trade) => {
      setTrades(prev => {
        const exists = prev.find(t => t._id === trade._id);
        if (exists) {
          return prev.map(t => t._id === trade._id ? trade : t);
        }
        return [trade, ...prev].slice(0, limit);
      });
    };

    const handleTradePending = (trade) => {
      setTrades(prev => {
        const exists = prev.find(t => t._id === trade._id);
        if (exists) {
          return prev.map(t => t._id === trade._id ? trade : t);
        }
        return [trade, ...prev].slice(0, limit);
      });
    };

    socketManager.on('tradeExecuted', handleTradeExecuted);
    socketManager.on('tradeFailed', handleTradeFailed);
    socketManager.on('tradePending', handleTradePending);

    return () => {
      socketManager.off('tradeExecuted', handleTradeExecuted);
      socketManager.off('tradeFailed', handleTradeFailed);
      socketManager.off('tradePending', handleTradePending);
    };
  }, [user?._id, autoScroll, limit]);

  const filteredTrades = trades.filter(t => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'failed') return t.status === 'failed' || t.status === 'rejected';
    return t.status === statusFilter;
  });

  const stats = {
    total: trades.length,
    filled: trades.filter(t => t.status === 'filled').length,
    failed: trades.filter(t => t.status === 'failed' || t.status === 'rejected').length,
    pending: trades.filter(t => t.status === 'pending' || t.status === 'submitted').length
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Trade Activity</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchTrades}
            disabled={loading}
            className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-4 mb-4 p-3 rounded-lg bg-dark-800/50">
        <div className="text-center">
          <p className="text-lg font-bold">{stats.total}</p>
          <p className="text-xs text-dark-400">Total</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-emerald-400">{stats.filled}</p>
          <p className="text-xs text-dark-400">Filled</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-red-400">{stats.failed}</p>
          <p className="text-xs text-dark-400">Failed</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-yellow-400">{stats.pending}</p>
          <p className="text-xs text-dark-400">Pending</p>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-dark-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input py-1 px-2 text-sm"
          >
            <option value="all">All</option>
            <option value="filled">Filled</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Trade List - Scrollable */}
      <div 
        ref={containerRef}
        className="max-h-[600px] overflow-y-auto rounded-lg border border-dark-700"
      >
        {loading && trades.length === 0 ? (
          <div className="p-8 text-center text-dark-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading trades...
          </div>
        ) : filteredTrades.length === 0 ? (
          <div className="p-8 text-center text-dark-400">
            <Info className="w-6 h-6 mx-auto mb-2" />
            No trades found. Trades will appear here when your copied traders make moves.
          </div>
        ) : (
          <div className="divide-y divide-dark-700">
            {filteredTrades.map((trade) => (
              <TradeRow
                key={trade._id}
                trade={trade}
                expanded={expandedId === trade._id}
                onToggle={() => setExpandedId(expandedId === trade._id ? null : trade._id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-3 flex items-center justify-between text-xs text-dark-400">
        <span>Showing {filteredTrades.length} of {trades.length} trades</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="rounded"
          />
          Auto-scroll on new trades
        </label>
      </div>
    </div>
  );
}
